import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { downloadDateiOderFehler } from '../lib/download';
import BelegAnsichtModal from './BelegAnsichtModal';
import { navigiereZuAuftrag, kannZuAuftragNavigieren } from '../lib/navigation';
import './RegieberichtForm.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// Parität zu backend/app/api/einsatz_api.py::DISPO_ROLLEN (dort erzwungen,
// hier nur ausgeblendet). Das Backend gated /abrechnen und /nachtrag seit der
// Fix-Welle selbst — beides zusammen, weil jede Schicht allein den anderen
// Kanal offen ließe.
const DISPO_ROLLEN = ['Admin', 'Geschäftsführer', 'Projektleiter'];

/**
 * Darf diese Rolle einen Regiebericht abrechnen bzw. als Nachtrag beauftragen?
 *
 * Bewusst fail-closed (unbekannte Rolle → nein): das sind geldwirksame
 * Aktionen, und die Karte hängt seit der Baustellen-Inversion auch im
 * Projekte-Tab, der für Projektmitarbeiter sichtbar ist.
 */
export function darfAbrechnen(userRolle?: string): boolean {
  if (!userRolle) return false;
  return userRolle === 'SuperAdmin' || DISPO_ROLLEN.includes(userRolle);
}

/**
 * Rolle des angemeldeten Benutzers aus dem localStorage-Eintrag, den App.tsx
 * beim Login schreibt ('auth_user'). Nur für Komponenten, die keine Props von
 * ManagementPage bekommen (AuftragsAkte); wo die Rolle als Prop verfügbar ist,
 * hat die Prop Vorrang.
 */
export function leseRolleAusSpeicher(): string | undefined {
  try {
    const roh = localStorage.getItem('auth_user');
    if (!roh) return undefined;
    const rolle = (JSON.parse(roh) as { rolle?: string })?.rolle;
    return typeof rolle === 'string' ? rolle : undefined;
  } catch {
    return undefined;
  }
}

export type RegieberichtStatus = 'ENTWURF' | 'UNTERSCHRIEBEN' | 'ABGERECHNET';

export interface RegieberichtZeile {
  id?: number;
  typ: 'ZEIT' | 'MATERIAL';
  referenz_id?: number | null;
  beschreibung: string | null;
  menge: number;
  einheit: string | null;
  preis_einzeln: number | null;
  betrag?: number | null;
  /**
   * Nur im Vorschlag gesetzt (nicht persistiert): weist darauf hin, dass der
   * vorgeschlagene Preis kein Verkaufspreis ist, z.B. weil der Artikel keinen
   * VK führt und ersatzweise der Kostenpreis der Entnahme vorgeschlagen wird.
   */
  hinweis?: string | null;
}

export interface Regiebericht {
  id: number;
  /** Auftrag, dem der Bericht zugeordnet ist – null bei einem (noch)
   * auftraglosen Baustellen-Bericht (Baustellen-Inversion, Task 7/9/13). */
  auftrag_id: number | null;
  auftrag_nr?: string | null;
  nachtrag_auftrag?: { auftrag_id: number; auftrag_nr: string } | null;
  /** Baustelle: bei einem Bericht am Auftrag die des Auftrags (vererbt), bei
   * einem auftraglosen Bericht die direkt zugewiesene. */
  projekt_id: number | null;
  einsatz_id: number | null;
  datum: string | null;
  bericht_nr: string;
  status: RegieberichtStatus;
  freitext: string | null;
  unterschrift_kunde?: string | null;
  hat_unterschrift: boolean;
  unterschrift_name: string | null;
  erstellt_von: string | null;
  erstellt_am: string | null;
  zeilen: RegieberichtZeile[];
  summe_netto: number;
}

const STATUS_LABEL: Record<RegieberichtStatus, string> = {
  ENTWURF: 'Entwurf',
  UNTERSCHRIEBEN: 'Unterschrieben',
  ABGERECHNET: 'Abgerechnet',
};

function formatDatum(iso?: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('de-DE');
  } catch {
    return iso;
  }
}

function formatEuro(wert?: number | null): string {
  if (wert === null || wert === undefined) return '–';
  return `${wert.toFixed(2).replace('.', ',')} €`;
}

function fehlertext(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return err.message;
  }
  return fallback;
}

const leereZeile = (typ: 'ZEIT' | 'MATERIAL'): RegieberichtZeile => ({
  typ,
  referenz_id: null,
  beschreibung: '',
  menge: 1,
  einheit: typ === 'ZEIT' ? 'Std.' : 'Stk.',
  preis_einzeln: null,
});

// ── Signatur-Canvas ────────────────────────────────────────────────────────

interface SignaturCanvasProps {
  onChange: (leer: boolean) => void;
}

export interface SignaturCanvasHandle {
  toDataURL: () => string;
  clear: () => void;
}

/**
 * Zeichenfläche für die Kundenunterschrift.
 *
 * Nutzt Pointer-Events (ein Handler für Maus, Stift und Finger) statt eines
 * getrennten Touch-/Mouse-Pfads. `touch-action: none` im CSS verhindert, dass
 * ein Wisch auf dem Tablet die Seite scrollt statt zu zeichnen.
 */
const SignaturCanvas = React.forwardRef<SignaturCanvasHandle, SignaturCanvasProps>(
  ({ onChange }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const zeichnet = useRef(false);
    const hatStrich = useRef(false);

    const ctx = () => canvasRef.current?.getContext('2d') || null;

    const punkt = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      // Canvas-Backing-Store kann größer sein als die CSS-Fläche.
      return {
        x: ((e.clientX - rect.left) / rect.width) * canvas.width,
        y: ((e.clientY - rect.top) / rect.height) * canvas.height,
      };
    };

    const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const c = ctx();
      if (!c) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      zeichnet.current = true;
      const p = punkt(e);
      c.beginPath();
      c.moveTo(p.x, p.y);
    };

    const bewegen = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!zeichnet.current) return;
      const c = ctx();
      if (!c) return;
      const p = punkt(e);
      c.lineTo(p.x, p.y);
      c.stroke();
      if (!hatStrich.current) {
        hatStrich.current = true;
        onChange(false);
      }
    };

    const ende = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!zeichnet.current) return;
      zeichnet.current = false;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* Pointer bereits freigegeben – irrelevant */
      }
    };

    useEffect(() => {
      const c = ctx();
      if (!c) return;
      c.lineWidth = 2.5;
      c.lineCap = 'round';
      c.lineJoin = 'round';
      c.strokeStyle = '#1a1a1a';
    }, []);

    React.useImperativeHandle(ref, () => ({
      toDataURL: () => canvasRef.current?.toDataURL('image/png') || '',
      clear: () => {
        const canvas = canvasRef.current;
        const c = ctx();
        if (!canvas || !c) return;
        c.clearRect(0, 0, canvas.width, canvas.height);
        hatStrich.current = false;
        onChange(true);
      },
    }));

    return (
      <canvas
        ref={canvasRef}
        className="rb-canvas"
        width={600}
        height={200}
        onPointerDown={start}
        onPointerMove={bewegen}
        onPointerUp={ende}
        onPointerCancel={ende}
        onPointerLeave={ende}
      />
    );
  }
);
SignaturCanvas.displayName = 'SignaturCanvas';

// ── Formular ───────────────────────────────────────────────────────────────

/**
 * Ein Regiebericht hängt an GENAU einem Ziel: einem Auftrag oder – solange
 * er noch keinem Auftrag zugeordnet ist – einer Baustelle (Projekt). Als
 * Union statt zwei unabhängiger optionaler Props modelliert, damit "genau
 * eins gesetzt" schon beim Aufrufer vom Compiler erzwungen wird, nicht nur
 * per Konvention zur Laufzeit.
 */
export type RegieberichtZiel =
  | { auftragId: number; projektId?: never }
  | { auftragId?: never; projektId: number };

type RegieberichtFormProps = RegieberichtZiel & {
  /** Vorhandenen Bericht öffnen; ohne ID wird beim Speichern einer angelegt. */
  regieberichtId?: number | null;
  authToken: string;
  /** Rolle des angemeldeten Benutzers — steuert die Abrechnungs-Aktionen. */
  userRolle?: string;
  onClose: () => void;
  /** Wird nach jeder persistierten Änderung gerufen, damit die Karte neu lädt. */
  onChanged?: () => void;
};

const RegieberichtForm: React.FC<RegieberichtFormProps> = ({
  auftragId, projektId, regieberichtId, authToken, userRolle, onClose, onChanged,
}) => {
  const [bericht, setBericht] = useState<Regiebericht | null>(null);
  const [zeilen, setZeilen] = useState<RegieberichtZeile[]>([]);
  const [freitext, setFreitext] = useState('');
  const [unterschriftName, setUnterschriftName] = useState('');
  const [canvasLeer, setCanvasLeer] = useState(true);

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [hinweis, setHinweis] = useState('');
  const [pdfLaedt, setPdfLaedt] = useState(false);
  const [belegAnsichtOffen, setBelegAnsichtOffen] = useState(false);

  const canvasRef = useRef<SignaturCanvasHandle | null>(null);
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const readOnly = bericht !== null && bericht.status !== 'ENTWURF';
  // Erfassen und Unterschreiben bleiben für alle offen (das ist die Arbeit
  // vor Ort); nur die geldwirksamen Abschlüsse hängen an der Rolle.
  const kannAbrechnen = darfAbrechnen(userRolle);

  const uebernehmeBericht = useCallback((daten: Regiebericht) => {
    setBericht(daten);
    setZeilen(daten.zeilen);
    setFreitext(daten.freitext || '');
    setUnterschriftName(daten.unterschrift_name || '');
  }, []);

  const laden = useCallback(async () => {
    if (!regieberichtId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get<Regiebericht>(
        `${API_URL}/api/regieberichte/${regieberichtId}`, { headers });
      uebernehmeBericht(res.data);
    } catch (err) {
      setError(fehlertext(err, 'Regiebericht konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regieberichtId, authToken, uebernehmeBericht]);

  useEffect(() => { laden(); }, [laden]);

  /** Legt bei Bedarf einen Entwurf an und gibt dessen ID zurück. */
  const sicherstellenBericht = useCallback(async (): Promise<Regiebericht> => {
    if (bericht) return bericht;
    const zielParams = auftragId != null ? { auftrag_id: auftragId } : { projekt_id: projektId };
    const res = await axios.post<Regiebericht>(`${API_URL}/api/regieberichte`, {
      ...zielParams,
      freitext: freitext || null,
      zeilen_uebernehmen: false,
    }, { headers });
    uebernehmeBericht(res.data);
    onChanged?.();
    return res.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bericht, auftragId, projektId, freitext, authToken, uebernehmeBericht, onChanged]);

  const vorschlaegeUebernehmen = async () => {
    setBusy(true);
    setError('');
    setHinweis('');
    try {
      const aktuell = await sicherstellenBericht();
      const zielParams = auftragId != null ? { auftrag_id: auftragId } : { projekt_id: projektId };
      const res = await axios.get(`${API_URL}/api/regieberichte/vorschlag`, {
        params: { ...zielParams, regiebericht_id: aktuell.id },
        headers,
      });
      const vorschlaege: RegieberichtZeile[] = res.data.zeilen || [];
      if (vorschlaege.length === 0) {
        setHinweis('Keine offenen Zeitbuchungen oder Materialentnahmen zu diesem Auftrag.');
        return;
      }
      // Bereits vorhandene Herkunfts-Referenzen nicht doppelt aufnehmen.
      const vorhanden = new Set(
        zeilen.filter(z => z.referenz_id != null).map(z => `${z.typ}#${z.referenz_id}`));
      const neu = vorschlaege.filter(v => !vorhanden.has(`${v.typ}#${v.referenz_id}`));
      setZeilen([...zeilen, ...neu]);
      setHinweis(`${neu.length} Zeile(n) übernommen – noch nicht gespeichert.`);
    } catch (err) {
      setError(fehlertext(err, 'Vorschläge konnten nicht geladen werden.'));
    } finally {
      setBusy(false);
    }
  };

  const zeileAendern = (index: number, feld: keyof RegieberichtZeile, wert: string) => {
    setZeilen(zeilen.map((z, i) => {
      if (i !== index) return z;
      if (feld === 'menge') return { ...z, menge: parseFloat(wert.replace(',', '.')) || 0 };
      if (feld === 'preis_einzeln') {
        return { ...z, preis_einzeln: wert === '' ? null : parseFloat(wert.replace(',', '.')) || 0 };
      }
      return { ...z, [feld]: wert };
    }));
  };

  const zeileEntfernen = (index: number) => setZeilen(zeilen.filter((_, i) => i !== index));

  /**
   * Persistiert Freitext + Zeilen und gibt den frischen Serverstand zurück.
   * Wirft bei Fehlern weiter — die Aufrufer entscheiden über die Meldung.
   */
  const speichereStand = async (): Promise<Regiebericht> => {
    const aktuell = await sicherstellenBericht();
    await axios.patch(`${API_URL}/api/regieberichte/${aktuell.id}`,
      { freitext: freitext || null }, { headers });
    const res = await axios.put<Regiebericht>(
      `${API_URL}/api/regieberichte/${aktuell.id}/zeilen`,
      { zeilen: zeilen.map(z => ({
          typ: z.typ,
          referenz_id: z.referenz_id ?? null,
          beschreibung: z.beschreibung || null,
          menge: z.menge,
          einheit: z.einheit || null,
          preis_einzeln: z.preis_einzeln,
        })) },
      { headers });
    uebernehmeBericht(res.data);
    onChanged?.();
    return res.data;
  };

  const zeilenSpeichern = async () => {
    setBusy(true);
    setError('');
    setHinweis('');
    try {
      await speichereStand();
      setHinweis('Gespeichert.');
    } catch (err) {
      setError(fehlertext(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  };

  const unterschreiben = async () => {
    setBusy(true);
    setError('');
    setHinweis('');
    try {
      // Erst speichern, dann unterschreiben — nie umgekehrt und nie ohne.
      // Sonst unterschreibt der Kunde auf dem Bildschirm einen Stand, den der
      // Server so nie gesehen hat: gerendert und archiviert würde der zuletzt
      // gespeicherte, und uebernehmeBericht() würde die lokalen Änderungen
      // danach kommentarlos überschreiben.
      const gespeichert = await speichereStand();
      if (gespeichert.zeilen.length === 0) {
        setError('Ein Regiebericht ohne Zeilen kann nicht unterschrieben werden.');
        return;
      }
      const bild = canvasRef.current?.toDataURL() || '';
      const res = await axios.post<Regiebericht>(
        `${API_URL}/api/regieberichte/${gespeichert.id}/unterschreiben`,
        { unterschrift_kunde: bild, unterschrift_name: unterschriftName },
        { headers });
      uebernehmeBericht(res.data);
      setHinweis('Unterschrieben und archiviert.');
      onChanged?.();
    } catch (err) {
      // Schlägt schon das Speichern fehl, wird NICHT unterschrieben.
      setError(fehlertext(err, 'Unterschreiben fehlgeschlagen — nichts wurde unterschrieben.'));
    } finally {
      setBusy(false);
    }
  };

  const abrechnen = async () => {
    if (!bericht) return;
    setBusy(true);
    setError('');
    setHinweis('');
    try {
      const res = await axios.post(
        `${API_URL}/api/regieberichte/${bericht.id}/abrechnen`, {}, { headers });
      uebernehmeBericht(res.data.regiebericht);
      // Unbewertete Zeilen kommen hier nicht mehr an: der Server lehnt das
      // Abrechnen dann mit 409 ab (statt 0-Euro-Positionen zu erzeugen) und
      // nennt in der Fehlermeldung die betroffenen Zeilen sowie den
      // Bewertungs-Weg. Das erledigt der catch-Zweig.
      setHinweis('Als Auftragspositionen übernommen.');
      onChanged?.();
    } catch (err) {
      setError(fehlertext(err, 'Abrechnen fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  };

  /** Regiebericht als PDF herunterladen; den Dateinamen liefert der Server. */
  const pdfHerunterladen = async () => {
    if (!bericht) return;
    setPdfLaedt(true);
    setError('');
    const fehler = await downloadDateiOderFehler(
      `${API_URL}/api/regieberichte/${bericht.id}/pdf`,
      `Regiebericht_${bericht.bericht_nr}.pdf`,
      'Regiebericht',
    );
    if (fehler) setError(fehler);
    setPdfLaedt(false);
  };

  /**
   * Baustellen-Bericht ohne eigenen Auftrag: erzeugt in einem Schritt einen
   * Nachtrags-Auftrag an der Baustelle und rechnet den Bericht direkt darauf
   * ab. Der Server lehnt mit 409 ab, wenn der Bericht bereits einem Auftrag
   * zugeordnet ist, noch nicht unterschrieben wurde, oder die Baustelle
   * keinen Kunden hat – alle drei Fälle laufen über fehlertext() in die
   * gleiche Fehleranzeige wie die übrigen Aktionen, statt still zu scheitern.
   */
  const nachtragErzeugen = async () => {
    if (!bericht) return;
    setBusy(true);
    setError('');
    setHinweis('');
    try {
      const res = await axios.post(
        `${API_URL}/api/regieberichte/${bericht.id}/nachtrag`, {}, { headers });
      uebernehmeBericht(res.data.regiebericht);
      const neuerAuftrag = res.data.auftrag?.auftrag_nr;
      setHinweis(neuerAuftrag
        ? `Nachtrag ${neuerAuftrag} erzeugt und abgerechnet.`
        : 'Nachtrag erzeugt und abgerechnet.');
      onChanged?.();
    } catch (err) {
      setError(fehlertext(err, 'Nachtrag konnte nicht erzeugt werden.'));
    } finally {
      setBusy(false);
    }
  };

  const summe = zeilen.reduce(
    (s, z) => s + (z.preis_einzeln === null ? 0 : z.menge * z.preis_einzeln), 0);
  const zeitZeilen = zeilen.filter(z => z.typ === 'ZEIT');
  const materialZeilen = zeilen.filter(z => z.typ === 'MATERIAL');
  // Bewusst gegen den LOKALEN Zeilenstand geprüft, nicht gegen den Serverstand:
  // unterschreiben() speichert vorher implizit, also zählt, was auf dem
  // Bildschirm steht — genau das, was der Kunde unterschreibt.
  const kannUnterschreiben =
    (bericht === null || bericht.status === 'ENTWURF') && zeilen.length > 0
    && !canvasLeer && unterschriftName.trim().length > 0;

  return (
    <div className="rb-overlay" role="dialog" aria-modal="true">
      <div className="rb-modal">
        <div className="rb-header">
          <span className="rb-title">
            {bericht ? `Regiebericht ${bericht.bericht_nr}` : 'Neuer Regiebericht'}
            {bericht && (
              <span className={`rb-status rb-status--${bericht.status.toLowerCase()}`}>
                {STATUS_LABEL[bericht.status]}
              </span>
            )}
          </span>
          <button type="button" className="rb-close" onClick={onClose} aria-label="Schließen">×</button>
        </div>

        <div className="rb-body">
          {loading && <div className="rb-info">Wird geladen…</div>}
          {error && <div className="rb-error">{error}</div>}
          {hinweis && <div className="rb-hinweis">{hinweis}</div>}

          {readOnly && (
            <div className="rb-info">
              Dieser Bericht ist {STATUS_LABEL[bericht!.status].toLowerCase()} und damit
              unveränderlich – ein vom Kunden gegengezeichneter Beleg darf nicht
              nachträglich geändert werden.
            </div>
          )}

          <label className="rb-label" htmlFor="rb-freitext">Bemerkungen</label>
          <textarea
            id="rb-freitext"
            className="rb-textarea"
            rows={3}
            value={freitext}
            disabled={readOnly || busy}
            placeholder="Was wurde gemacht? Besonderheiten vor Ort?"
            onChange={e => setFreitext(e.target.value)}
          />

          <div className="rb-zeilen-kopf">
            <span className="rb-label">Zeilen</span>
            {!readOnly && (
              <div className="rb-zeilen-aktionen">
                <button type="button" className="rb-btn" disabled={busy}
                        onClick={vorschlaegeUebernehmen}>
                  Vorschläge übernehmen
                </button>
                <button type="button" className="rb-btn" disabled={busy}
                        onClick={() => setZeilen([...zeilen, leereZeile('ZEIT')])}>
                  + Zeit
                </button>
                <button type="button" className="rb-btn" disabled={busy}
                        onClick={() => setZeilen([...zeilen, leereZeile('MATERIAL')])}>
                  + Material
                </button>
              </div>
            )}
          </div>

          {zeilen.length === 0 ? (
            <div className="rb-leer">Noch keine Zeilen.</div>
          ) : (
            <table className="rb-tabelle">
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Beschreibung</th>
                  <th className="rb-num">Menge</th>
                  <th>Einheit</th>
                  <th className="rb-num">Einzelpreis</th>
                  <th className="rb-num">Gesamt</th>
                  {!readOnly && <th />}
                </tr>
              </thead>
              <tbody>
                {zeilen.map((z, i) => (
                  <tr key={z.id ?? `neu-${i}`}>
                    <td>
                      <span className={`rb-typ rb-typ--${z.typ.toLowerCase()}`}>
                        {z.typ === 'ZEIT' ? 'Zeit' : 'Material'}
                      </span>
                    </td>
                    <td>
                      <input
                        className="rb-input"
                        value={z.beschreibung || ''}
                        disabled={readOnly || busy}
                        onChange={e => zeileAendern(i, 'beschreibung', e.target.value)}
                      />
                    </td>
                    <td className="rb-num">
                      <input
                        className="rb-input rb-input--num"
                        type="number" step="0.01" min="0"
                        value={z.menge}
                        disabled={readOnly || busy}
                        onChange={e => zeileAendern(i, 'menge', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="rb-input rb-input--kurz"
                        value={z.einheit || ''}
                        disabled={readOnly || busy}
                        onChange={e => zeileAendern(i, 'einheit', e.target.value)}
                      />
                    </td>
                    <td className="rb-num">
                      <input
                        className={`rb-input rb-input--num${z.hinweis ? ' rb-input--warn' : ''}`}
                        type="number" step="0.01" min="0"
                        value={z.preis_einzeln ?? ''}
                        placeholder="–"
                        disabled={readOnly || busy}
                        onChange={e => zeileAendern(i, 'preis_einzeln', e.target.value)}
                      />
                      {z.hinweis && (
                        <span className="rb-preis-warn" title={z.hinweis}>⚠ Preis prüfen</span>
                      )}
                    </td>
                    <td className="rb-num">
                      {z.preis_einzeln === null ? '–' : formatEuro(z.menge * z.preis_einzeln)}
                    </td>
                    {!readOnly && (
                      <td>
                        <button type="button" className="rb-btn rb-btn--danger"
                                disabled={busy} onClick={() => zeileEntfernen(i)}
                                aria-label="Zeile entfernen">×</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="rb-summen">
            <span>{zeitZeilen.length} Zeitzeile(n) · {materialZeilen.length} Materialzeile(n)</span>
            <span className="rb-summe">Summe netto: {formatEuro(summe)}</span>
          </div>

          <div className="rb-signatur">
            <span className="rb-label">Unterschrift Kunde</span>
            {readOnly ? (
              <div className="rb-signatur-readonly">
                {bericht?.unterschrift_kunde ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img className="rb-signatur-bild" src={bericht.unterschrift_kunde}
                       alt="Unterschrift des Kunden" />
                ) : (
                  <div className="rb-leer">Kein Unterschriftsbild hinterlegt.</div>
                )}
                <div className="rb-signatur-meta">
                  {bericht?.unterschrift_name} · {formatDatum(bericht?.datum)}
                </div>
              </div>
            ) : (
              <>
                <SignaturCanvas ref={canvasRef} onChange={setCanvasLeer} />
                <div className="rb-signatur-zeile">
                  <input
                    className="rb-input"
                    placeholder="Name des Unterzeichners"
                    value={unterschriftName}
                    disabled={busy}
                    onChange={e => setUnterschriftName(e.target.value)}
                  />
                  <button type="button" className="rb-btn" disabled={busy}
                          onClick={() => canvasRef.current?.clear()}>
                    Löschen
                  </button>
                </div>
                <div className="rb-leer">
                  {zeilen.length === 0
                    ? 'Erst eine Zeile erfassen – ein Bericht ohne Zeilen kann nicht unterschrieben werden.'
                    : 'Offene Änderungen an Zeilen und Bemerkungen werden beim Unterschreiben automatisch gespeichert – unterschrieben wird genau das, was hier steht.'}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="rb-footer">
          {bericht && (
            <button type="button" className="rb-btn" disabled={busy || pdfLaedt}
                    onClick={pdfHerunterladen}>
              {pdfLaedt ? '… lädt' : 'PDF herunterladen'}
            </button>
          )}
          {bericht && (
            <button type="button" className="rb-btn" disabled={busy}
                    onClick={() => setBelegAnsichtOffen(true)}>
              Beleg-Ansicht
            </button>
          )}
          <div className="rb-footer-rechts">
            {!readOnly && (
              <>
                <button type="button" className="rb-btn" disabled={busy}
                        onClick={zeilenSpeichern}>
                  {busy ? 'Speichert…' : 'Speichern'}
                </button>
                <button type="button" className="rb-btn rb-btn--primary"
                        disabled={busy || !kannUnterschreiben}
                        title={kannUnterschreiben
                          ? 'Offene Änderungen werden beim Unterschreiben gespeichert.'
                          : 'Zeile erfassen, unterschreiben lassen und Namen eintragen'}
                        onClick={unterschreiben}>
                  Unterschreiben & abschließen
                </button>
              </>
            )}
            {bericht && bericht.status === 'UNTERSCHRIEBEN' && kannAbrechnen && (
              bericht.auftrag_id != null ? (
                <button type="button" className="rb-btn rb-btn--primary" disabled={busy}
                        onClick={abrechnen}>
                  Abrechnen
                </button>
              ) : (
                // Auftragsloser Baustellen-Bericht (Ruling 1, Task 13): kein
                // Auftrags-Wahldialog in dieser Welle - wer einen bestehenden
                // Auftrag will, nutzt Agent/API. Hier nur der Nachtrag-Weg.
                <button type="button" className="rb-btn rb-btn--primary" disabled={busy}
                        onClick={nachtragErzeugen}>
                  Nachtrag erzeugen &amp; abrechnen
                </button>
              )
            )}
            {bericht && bericht.status === 'UNTERSCHRIEBEN' && !kannAbrechnen && (
              <span className="rb-info">
                Abrechnung und Nachtrag übernimmt die Projektleitung.
              </span>
            )}
            <button type="button" className="rb-btn" onClick={onClose}>Schließen</button>
          </div>
        </div>
      </div>

      {belegAnsichtOffen && bericht && (
        <BelegAnsichtModal
          url={`${API_URL}/api/regieberichte/${bericht.id}/beleg-ansicht`}
          title={`Regiebericht ${bericht.bericht_nr}`}
          kontext={{}}
          onClose={() => setBelegAnsichtOffen(false)}
        />
      )}
    </div>
  );
};

// ── Karte für die Auftragsakte ─────────────────────────────────────────────

type RegieberichtKarteProps = RegieberichtZiel & {
  authToken: string;
  /** Rolle des angemeldeten Benutzers — durchgereicht an das Formular, das
   *  daran die Abrechnungs-Aktionen ausblendet. */
  userRolle?: string;
};

/**
 * Einstieg in Auftragsakte UND Baustellen-Detail: Liste der Berichte +
 * „Neuer Regiebericht". Bewusst selbstladend (wie DokumenteKarte), damit die
 * jeweilige Ladefunktion des Elternteils nicht angefasst werden muss.
 */
export const RegieberichtKarte: React.FC<RegieberichtKarteProps> = ({ auftragId, projektId, authToken, userRolle }) => {
  const [berichte, setBerichte] = useState<Regiebericht[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offen, setOffen] = useState<{ id: number | null } | null>(null);

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  // Aus den eigenen (sich gegenseitig ausschließenden) Props neu zusammengesetzt,
  // damit die RegieberichtForm-Instanz unten typsicher exakt eine der beiden
  // Props weitergereicht bekommt - eine einfache Doppel-Destrukturierung
  // würde den "genau eins gesetzt"-Bezug zwischen auftragId/projektId verlieren.
  const ziel: RegieberichtZiel = auftragId != null
    ? { auftragId }
    // Durch die Union in RegieberichtKarteProps ist projektId hier zur
    // Laufzeit immer gesetzt - dem Compiler ist der Zusammenhang zwischen
    // den zwei destrukturierten Variablen aber nicht mehr ansehbar.
    : { projektId: projektId as number };

  const laden = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const zielParams = auftragId != null ? { auftrag_id: auftragId } : { projekt_id: projektId };
      const res = await axios.get<Regiebericht[]>(`${API_URL}/api/regieberichte`, {
        params: zielParams, headers,
      });
      setBerichte(res.data);
    } catch {
      setError('Regieberichte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auftragId, projektId, authToken]);

  useEffect(() => { laden(); }, [laden]);

  return (
    <div className="aa-card">
      <div className="aa-card-header">
        <h3 className="aa-card-title">
          Regieberichte{berichte.length > 0 ? ` (${berichte.length})` : ''}
        </h3>
        <button type="button" className="rb-btn" onClick={() => setOffen({ id: null })}>
          + Neuer Regiebericht
        </button>
      </div>

      {error && <div className="rb-error">{error}</div>}
      {loading ? (
        <p className="aa-empty">Wird geladen…</p>
      ) : berichte.length === 0 ? (
        <p className="aa-empty">Keine Regieberichte</p>
      ) : (
        <ul className="aa-liste">
          {berichte.map(b => (
            <li key={b.id} className="aa-liste-item rb-liste-item"
                onClick={() => setOffen({ id: b.id })}>
              <span className="aa-liste-nr">{b.bericht_nr}</span>
              <span className={`rb-status rb-status--${b.status.toLowerCase()}`}>
                {STATUS_LABEL[b.status]}
              </span>
              <span className="aa-liste-datum">{formatDatum(b.datum)}</span>
              <span className="rb-liste-summe">{formatEuro(b.summe_netto)}</span>
              {b.nachtrag_auftrag && (
                kannZuAuftragNavigieren() ? (
                  <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                          title="Zum Nachtrags-Angebot"
                          onClick={e => { e.stopPropagation(); navigiereZuAuftrag(b.nachtrag_auftrag!.auftrag_id); }}>
                    → {b.nachtrag_auftrag.auftrag_nr}
                  </button>
                ) : (
                  // Rolle sieht den Aufträge-Tab nicht — kein toter Link.
                  <span className="rb-liste-nr-plain">{b.nachtrag_auftrag.auftrag_nr}</span>
                )
              )}
              {/* Selbstreferenz unterdrücken: innerhalb der Auftragsakte DIESES
                  Auftrags wäre "→ zu Auftrag X" derselbe Auftrag, den man
                  gerade betrachtet. Im Projekt-Kontext und im Regieberichte-Tab
                  (auftragId nicht gesetzt bzw. abweichend) bleibt der Chip. */}
              {b.status === 'ABGERECHNET' && b.auftrag_id != null && b.auftrag_id !== auftragId && (
                kannZuAuftragNavigieren() ? (
                  <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                          title="Zum abgerechneten Auftrag"
                          onClick={e => { e.stopPropagation(); navigiereZuAuftrag(b.auftrag_id as number); }}>
                    → {b.auftrag_nr || `#${b.auftrag_id}`}
                  </button>
                ) : (
                  // Rolle sieht den Aufträge-Tab nicht — kein toter Link.
                  <span className="rb-liste-nr-plain">{b.auftrag_nr || `#${b.auftrag_id}`}</span>
                )
              )}
            </li>
          ))}
        </ul>
      )}

      {offen && (
        <RegieberichtForm
          {...ziel}
          regieberichtId={offen.id}
          authToken={authToken}
          userRolle={userRolle}
          onClose={() => { setOffen(null); laden(); }}
          onChanged={laden}
        />
      )}
    </div>
  );
};

export default RegieberichtForm;
